// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {useStoreAccess} from '@app/features/store/state/useStoreAccess';
import {STORE_COPY} from '@app/features/store/utils/StoreCopy';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Spinner} from '@app/features/ui/components/Spinner';
import {AvatarDecorationOverlay} from '@app/features/user/components/AvatarDecorationOverlay';
import {CosmeticPickerPreview} from '@app/features/user/components/modals/tabs/my_profile_tab/CosmeticPickerPreview';
import shared from '@app/features/user/components/modals/tabs/my_profile_tab/CosmeticPickerPreview.module.css';
import Users from '@app/features/user/state/Users';
import {CheckIcon} from '@phosphor-icons/react';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/AvatarDecorationPickerModal.module.css';
import AvatarDecorationCatalog from '@app/features/user/state/AvatarDecorationCatalog';
import {
	getAvatarDecorationDisplayName,
	getAvatarDecorationSearchHaystack,
} from '@app/features/user/utils/AvatarDecorationCopy';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useMemo, useState} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Choose an avatar decoration',
	comment: 'Title of the avatar decoration picker modal.',
});
const SEARCH_DESCRIPTOR = msg({
	message: 'Search avatar decorations',
	comment: 'Placeholder for filtering avatar decorations in the picker modal.',
});
const NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: 'Option that clears the equipped avatar decoration.',
});
const EMPTY_DESCRIPTOR = msg({
	message: 'No avatar decorations match that search.',
	comment: 'Empty state when the avatar decoration picker filter has no results.',
});
const CATALOG_EMPTY_DESCRIPTOR = msg({
	message: 'No avatar decorations are available yet.',
	comment: 'Empty state when the hosted avatar decoration catalog has no entries.',
});
const HINT_DESCRIPTOR = msg({
	message: 'Shown around your avatar on profiles. Requires {premiumProductName}.',
	comment: 'Helper text in the avatar decoration picker modal. Preserve {premiumProductName}; it is inserted by code.',
});
const DONE_DESCRIPTOR = msg({
	message: 'Done',
	comment: 'Closes the avatar decoration picker modal.',
});

interface AvatarDecorationPickerModalProps {
	value: string | null;
	onChange: (decorationId: string | null) => void;
}

export const AvatarDecorationPickerModal = observer(function AvatarDecorationPickerModal({
	value,
	onChange,
}: AvatarDecorationPickerModalProps) {
	const {i18n} = useLingui();
	const [query, setQuery] = useState('');
	const [selectedId, setSelectedId] = useState<string | null>(value);
	/** undefined = not hovering; '' = hovering "None". */
	const [hoveredId, setHoveredId] = useState<string | undefined>(undefined);
	const selectDecoration = (decorationId: string | null) => {
		setSelectedId(decorationId);
		onChange(decorationId);
	};
	const storeAccess = useStoreAccess();
	const accessible = AvatarDecorationCatalog.decorations.filter(
		(entry) => entry.accessible !== false && storeAccess.canUse('avatar_decoration', entry.id),
	);
	const filtered = useMemo(() => {
		const needle = query.trim().toLowerCase();
		if (!needle) {
			return accessible;
		}
		return accessible.filter((entry) => getAvatarDecorationSearchHaystack(entry, i18n.locale).includes(needle));
	}, [accessible, i18n.locale, query]);
	return (
		<Modal.Root onClose={() => ModalCommands.pop()} size="large" data-flx="user.avatar-decoration-picker-modal.root">
			<Modal.ScreenReaderLabel
				text={i18n._(TITLE_DESCRIPTOR)}
				data-flx="user.avatar-decoration-picker-modal.screen-reader-label"
			/>
			<Modal.Header title={i18n._(TITLE_DESCRIPTOR)} data-flx="user.avatar-decoration-picker-modal.header" />
			<Modal.Content data-flx="user.avatar-decoration-picker-modal.content">
				<div className={shared.layout}>
					<div className={shared.listPane}>
						<p className={styles.hint}>{i18n._(HINT_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}</p>
						{accessible.length > 6 ? (
							<Input
								value={query}
								onChange={(event) => setQuery(event.target.value)}
								placeholder={i18n._(SEARCH_DESCRIPTOR)}
								className={styles.search}
								data-flx="user.avatar-decoration-picker-modal.search"
							/>
						) : null}
						{storeAccess.loading ? (
							<div className={styles.empty}>
								<Spinner />
							</div>
						) : accessible.length === 0 ? (
							<div className={styles.empty}>{i18n._(CATALOG_EMPTY_DESCRIPTOR)}</div>
						) : filtered.length === 0 ? (
							<div className={styles.empty}>{i18n._(EMPTY_DESCRIPTOR)}</div>
						) : (
							<div className={clsx(shared.grid, styles.grid)} onMouseLeave={() => setHoveredId(undefined)}>
								<button
									type="button"
									className={clsx(shared.tile, selectedId == null && shared.tileSelected)}
									onClick={() => selectDecoration(null)}
									onMouseEnter={() => setHoveredId('')}
									data-flx="user.avatar-decoration-picker-modal.none"
								>
									{selectedId == null ? (
										<span className={shared.check}>
											<CheckIcon weight="bold" className={shared.checkIcon} />
										</span>
									) : null}
									<div className={styles.preview}>
										<div className={styles.avatarStandIn} />
									</div>
									<div className={shared.tileLabel}>{i18n._(NONE_DESCRIPTOR)}</div>
								</button>
								{filtered.map((entry) => (
									<button
										key={entry.id}
										type="button"
										className={clsx(shared.tile, selectedId === entry.id && shared.tileSelected)}
										onClick={() => selectDecoration(entry.id)}
										onMouseEnter={() => setHoveredId(entry.id)}
										data-flx="user.avatar-decoration-picker-modal.option"
									>
										{selectedId === entry.id ? (
											<span className={shared.check}>
												<CheckIcon weight="bold" className={shared.checkIcon} />
											</span>
										) : null}
										<div className={styles.preview}>
											<div className={styles.avatarStandIn} />
											<AvatarDecorationOverlay userId="" decorationId={entry.id} active={hoveredId === entry.id} />
										</div>
										<div className={shared.tileLabel}>{getAvatarDecorationDisplayName(entry, i18n.locale)}</div>
									</button>
								))}
							</div>
						)}
					</div>
					{(() => {
						const previewId = hoveredId !== undefined ? hoveredId : (selectedId ?? '');
						const previewEntry = previewId ? AvatarDecorationCatalog.getById(previewId) : undefined;
						const me = Users.currentUser;
						return (
							<CosmeticPickerPreview
								decorationId={previewId}
								nameplateId={me ? (Users.getNameplateId(me.id) ?? null) : null}
								itemName={previewEntry ? getAvatarDecorationDisplayName(previewEntry, i18n.locale) : i18n._(NONE_DESCRIPTOR)}
								itemDescription={previewEntry?.description ?? null}
							/>
						);
					})()}
				</div>
			</Modal.Content>
			<Modal.Footer data-flx="user.avatar-decoration-picker-modal.footer">
				{storeAccess.storeMode && (
					<Button
						variant="secondary"
						onClick={() => {
							ModalCommands.popAll();
							RouterUtils.transitionTo(Routes.STORE);
						}}
						data-flx="user.cosmetic-picker.open-store"
					>
						{i18n._(STORE_COPY.title)}
					</Button>
				)}
				<Button variant="primary" onClick={() => ModalCommands.pop()} data-flx="user.avatar-decoration-picker-modal.done">
					{i18n._(DONE_DESCRIPTOR)}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
